import React, { useEffect, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import CancelIcon from "@mui/icons-material/Cancel";
import { IconButton, Box, Grid } from "@mui/material";
import { useSnackbar } from "notistack";

const baseStyle = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "20px",
  borderWidth: 2,
  borderRadius: 2,
  borderColor: "#eeeeee",
  borderStyle: "dashed",
  backgroundColor: "#fafafa",
  color: "#bdbdbd",
  outline: "none",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
};

const activeStyle = {
  borderColor: "#2196f3",
};

const acceptStyle = {
  borderColor: "#00e676",
};

const rejectStyle = {
  borderColor: "#ff1744",
};

const thumbsContainer = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
};

const container = {
  display: "block",
  position: "relative",
};

const thumb = {
  borderRadius: 2,
  border: "1px solid #eaeaea",
  marginRight: 8,
  width: "auto",
  boxSizing: "border-box",
};

const thumbInner = {
  display: "flex",
  width: 200,
  height: 100,
  overflow: "hidden",
};

const img = {
  display: "block",
  width: "100%",
  height: "auto",
  objectFit: "contain",
};

const ESignatureDropzone = ({
  setValue,
  files,
  setFiles,
  valueToSet,
  manualRevoke,
  filesLimit,
  disabled,
  acceptedFile,
  maxSize,
}) => {
  const acceptFileType = (acceptedFile) => {
    if (acceptedFile === "image") {
      return {
        "image/*": [],
      };
    } else {
      return {
        "image/*": [],
        "application/pdf": [],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
      };
    }
  };

  function formatBytes(bytes, decimals = 2) {
    if (!+bytes) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }

  const { enqueueSnackbar } = useSnackbar();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: acceptFileType(acceptedFile),
    maxFiles: filesLimit ? filesLimit : 0,
    maxSize: maxSize,
    onDrop: (acceptedFiles, fileRejections) => {
      // Handle rejected files
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === "file-too-large") {
            enqueueSnackbar(
              `ไม่สามารถอัปโหลดได้\nเนื่องจากไฟล์มีขนาดใหญ่เกิน ${formatBytes(
                maxSize
              )}`,
              { variant: "warning", style: { whiteSpace: "pre-line" } }
            );
          }
          if (err.code === "too-many-files") {
            enqueueSnackbar(`อัปโหลดได้สูงสุด ${filesLimit} ไฟล์`, {
              variant: "warning",
            });
          }
        });
      });

      if (filesLimit && files.length >= filesLimit) {
        enqueueSnackbar("เพิ่มรูปภาพได้สูงสุดหนึ่งรูป", {
          variant: "warning",
        });
        return;
      }
      setFiles((prev) => [
        ...prev,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
    disabled: disabled,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const removeFile = (file) => () => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
  };

  const thumbs = files.map((file, index) => {
    if (!file) {
      return null;
    }
    return (
      <div style={container} key={index}>
        <div style={thumb}>
          <div style={thumbInner}>
            {typeof file !== "object" && (
              <img
                alt={file}
                src={file}
                style={img}
                // Revoke data uri after image is loaded
                onLoad={
                  !manualRevoke
                    ? () => {
                        URL.revokeObjectURL(file);
                      }
                    : () => {}
                }
              />
            )}
            {file.preview && (
              <img
                alt={file?.name || file}
                src={file?.preview || file}
                style={img}
                // Revoke data uri after image is loaded
                onLoad={
                  !manualRevoke
                    ? () => {
                        URL.revokeObjectURL(file.preview);
                      }
                    : () => {}
                }
              />
            )}
            {file.url && (
              <img
                alt={file?.name || file}
                src={file?.url || file}
                style={img}
                // Revoke data uri after image is loaded
                onLoad={
                  !manualRevoke
                    ? () => {
                        URL.revokeObjectURL(file.url);
                      }
                    : () => {}
                }
              />
            )}
          </div>
        </div>
        {!disabled && (
          <IconButton
            aria-label="cancel"
            onClick={removeFile(file)}
            sx={{ position: "absolute", top: -20, right: -12 }}
            color="primary"
          >
            <CancelIcon />
          </IconButton>
        )}
      </div>
    );
  });

  useEffect(() => {
    setValue(valueToSet, files);
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => {
      if (!manualRevoke) {
        files.forEach((file) => URL.revokeObjectURL(file.preview));
      }
    };
  }, [files, setValue, valueToSet, manualRevoke]);

  return (
    <Box display="flex">
      <Grid container spacing={2} alignItems="center">
        {!disabled && (
          <Grid item xs={6} sm={6} md={4}>
            <div {...getRootProps({ style })}>
              <input {...getInputProps()} />
              <p>ลากมาวางเพื่ออัปโหลด</p>
            </div>
          </Grid>
        )}
        <Grid item xs={6}>
          <aside style={thumbsContainer}>{thumbs}</aside>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ESignatureDropzone;
